import { EsriBoundedBy, EsriGeneratedRow, EsriLocation } from '../api/ach/esri';

// CSV in both directions for the ESRI builder. toLocationCsv writes the platform
// location-upload file from /esri/generate rows, matching the ESRI POC's
// ResultsPanel.tsx byte for byte: the 22 columns Ventura's CsvLocationUploader
// reads, in its order, with the unused ones left empty. parseLocationCsv reads an
// uploaded file of points (lat/lon/radius, with the POC's lenient header names)
// to send to /esri/generate, so a platform location file works as input too.

export const LOCATION_FILE_HEADERS = [
  'name',
  'address',
  'latitude',
  'longitude',
  'radius',
  'zipcode',
  'street',
  'location_type',
  'state',
  'city',
  'dma',
  'congressional_district',
  'lv1',
  'lv2',
  'lv3',
  'lv4',
  'lv5',
  'lv6',
  'targeting_by',
  'bounded_by',
  'excluded',
  'preceptivity_disabled',
];

const escapeCsv = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined) return '';
  const str = String(value);
  return /[",\n\r]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
};

export const toLocationCsv = (rows: EsriGeneratedRow[], boundedBy: EsriBoundedBy): string => {
  const lines = [LOCATION_FILE_HEADERS.join(',')];
  rows.forEach((row) => {
    const values = [
      row.name,
      '', // address
      row.latitude,
      row.longitude,
      row.radius,
      row.zip,
      row.street,
      row.location_type,
      row.state,
      row.city,
      row.dma,
      '', // congressional_district
      '', '', '', '', '', '', // lv1-lv6
      '', // targeting_by
      boundedBy,
      '', // excluded
      '', // preceptivity_disabled
    ];
    lines.push(values.map(escapeCsv).join(','));
  });
  return lines.join('\n');
};

export const toLocationCsvFile = (rows: EsriGeneratedRow[], boundedBy: EsriBoundedBy, date = new Date()): File =>
  new File([toLocationCsv(rows, boundedBy)], `esri_location_file_${date.toISOString().slice(0, 10)}.csv`, {
    type: 'text/csv',
  });

// ACH's /esri/generate limit (MaxLocationRows).
export const MAX_UPLOAD_LOCATIONS = 20000;

const HEADER_ALIASES: Record<string, keyof EsriLocation> = {
  latitude: 'latitude',
  lat: 'latitude',
  longitude: 'longitude',
  lon: 'longitude',
  lng: 'longitude',
  long: 'longitude',
  radius: 'radius',
  rad: 'radius',
  name: 'name',
  location_name: 'name',
  locationname: 'name',
  loc_name: 'name',
  location: 'name',
};

const REQUIRED_COLUMNS: (keyof EsriLocation)[] = ['latitude', 'longitude', 'radius'];

// RFC 4180: quoted fields may hold commas, doubled quotes and newlines.
const parseCsvRecords = (text: string, delimiter: string): string[][] => {
  const records: string[][] = [];
  let record: string[] = [];
  let field = '';
  let quoted = false;
  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    if (quoted) {
      if (char === '"' && text[i + 1] === '"') {
        field += '"';
        i += 1;
      } else if (char === '"') {
        quoted = false;
      } else {
        field += char;
      }
    } else if (char === '"') {
      quoted = true;
    } else if (char === delimiter) {
      record.push(field);
      field = '';
    } else if (char === '\n' || char === '\r') {
      if (char === '\r' && text[i + 1] === '\n') i += 1;
      record.push(field);
      records.push(record);
      record = [];
      field = '';
    } else {
      field += char;
    }
  }
  record.push(field);
  records.push(record);
  return records.filter((r) => r.some((value) => value.trim() !== ''));
};

export interface ParsedLocations {
  locations: EsriLocation[];
  errors: string[];
}

export const parseLocationCsv = (text: string): ParsedLocations => {
  const body = text.replace(/^\uFEFF/, '');
  const [firstLine] = body.split(/\r?\n/);
  const delimiter = firstLine.includes('\t') && !firstLine.includes(',') ? '\t' : ',';
  const [header = [], ...records] = parseCsvRecords(body, delimiter);
  const columns: Partial<Record<keyof EsriLocation, number>> = {};
  header.forEach((name, index) => {
    const field =
      HEADER_ALIASES[
        name
          .trim()
          .toLowerCase()
          .replace(/[^a-z_]/g, '')
      ];
    if (field && columns[field] === undefined) columns[field] = index;
  });

  const missing = REQUIRED_COLUMNS.filter((field) => columns[field] === undefined);
  if (missing.length > 0) {
    return {
      locations: [],
      errors: [`Missing required column(s): ${missing.join(', ')}. Found: ${header.join(', ') || 'none'}`],
    };
  }

  const errors: string[] = [];
  const locations: EsriLocation[] = [];
  records.forEach((record, index) => {
    const line = index + 2;
    const [latitude, longitude, radius] = REQUIRED_COLUMNS.map((field) =>
      Number(record[columns[field]]?.trim() || NaN)
    );
    if ([latitude, longitude, radius].some(Number.isNaN)) {
      errors.push(`Row ${line}: latitude, longitude and radius must be numbers`);
    } else if (latitude < -90 || latitude > 90) {
      errors.push(`Row ${line}: latitude ${latitude} is outside -90 to 90`);
    } else if (longitude < -180 || longitude > 180) {
      errors.push(`Row ${line}: longitude ${longitude} is outside -180 to 180`);
    } else if (radius <= 0) {
      errors.push(`Row ${line}: radius must be positive`);
    } else {
      const name = columns.name === undefined ? '' : (record[columns.name] ?? '').trim();
      locations.push({ name, latitude, longitude, radius });
    }
  });

  if (locations.length > MAX_UPLOAD_LOCATIONS) {
    errors.push(
      `Only the first ${MAX_UPLOAD_LOCATIONS.toLocaleString()} of ${locations.length.toLocaleString()} locations are used`
    );
    locations.length = MAX_UPLOAD_LOCATIONS;
  }
  if (locations.length === 0 && errors.length === 0) errors.push('The file has no location rows');
  return { locations, errors };
};
